import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getAnonymousID, getSessionID } from 'redline-client-sdk';

import { LoaderCircular } from '@common/components/LoaderCircular';
import { useWebBridgeContext } from '@libs/app-bridge';
import { useUserContext } from '@common/contexts/UserContext';
import { titleMap } from '@common/constants/RouteMap';
import { StorageMap } from '@common/constants/StorageMap';
import { useGoogleAnalytics } from '@libs/utils/hooks/useGoogleAnalytics';
import { setOnStorage } from '@common/utils/handleStorage';
import Router from '@common/routes/Router';

import { useAuthentication } from '@libs/acp/contexts/Authentication';
import { usePartnerAuthentication } from '@libs/acp/contexts/PartnerAuthentication';

import { useRedline } from '@libs/redline';
import useHotjar from '@libs/utils/hooks/useHotjar';
import { isProd } from '@libs/utils/helpers/isProd';
import { addCookie, getCookie, getCookiesStartsWith } from '@libs/utils/helpers/getSafeCookies';

import { ClarityHelpers, untilLoad } from '@consumidor-positivo/ts-utils';

import { identityLoginEnrichment } from '@libs/acp/apis/signIn/signInAPI';

import 'iq-blueberry/dist/styles.css';
import 'react-spinetic/styles.css';

const identifyClarity = async () => {
  await untilLoad(() => window?.cpRedline?.sessionID, 10000);
  ClarityHelpers.identify({
    userId: getAnonymousID() || 'not-loaded',
    sessionId: getSessionID() || 'not-loaded',
    pageId: window.location.pathname,
  });
};

function App() {
  // Hooks
  const { track } = useRedline();
  const { appSessionTracking } = useWebBridgeContext();
  const { trackGA } = useGoogleAnalytics();
  const anonymousId = getAnonymousID();
  const hotjar = useHotjar();
  const { isLoading: isPartnerLoading, isAuthenticated: isPartnerAuthenticated } = usePartnerAuthentication();
  const { isLoading, isAuthenticated } = useAuthentication();
  const { signOut } = useUserContext();

  // States
  const [sentForcedSessionStarted, setSendForcedSessionStarted] = useState(false);

  // --- Functions ---
  function sendEventExperimentsDogx() {
    const dxCookies = getCookiesStartsWith('dx-');
    const { 'dx-bg': removedProp, ...dxCookiesOnly } = dxCookies;
    const hasTest = Object.keys(dxCookiesOnly).length;

    if (hasTest) {
      const experiments: string[] = [];
      const experimentsResults: string[] = [];

      const dxExperiments = Object.keys(dxCookiesOnly)
        .map((name) => {
          const dxValues = dxCookiesOnly[name]?.split(':');
          if (!dxValues) return null;

          const [variant, version, percentage] = dxValues;

          experiments.push(name);
          experimentsResults.push(variant);

          return {
            name,
            variant,
            version: Number(version),
            percentage: Number(percentage),
          };
        })
        .filter((experiment) => experiment !== null);

      track.dogx.experimentsLoaded({
        experiments: dxExperiments,
      });

      hotjar.add({
        anonymousId: anonymousId,

        sessionExperiments: experiments.join('|').slice(0, 199),
        sessionVariants: experimentsResults.join('|').slice(0, 199),
      });
    } else {
      hotjar.add({ anonymousId: anonymousId });

      track.dogx.experimentsLoaded({
        experiments: [
          {
            name: 'dx-not-found',
            variant: 'not-found',
            percent: 100,
            version: 1,
          },
        ],
      });
    }
  }

	// Partner iframe
	const isRenderInPartner = window !== window.parent;

  async function forceSessionStarted() {
    await untilLoad(() => window?.cpRedline?.ready, 7000);
    if (!sentForcedSessionStarted) {
      window?.cpRedline?.triggerSessionStarted();
      setSendForcedSessionStarted(true);
    }
  }

  // --- Effects ---
  // Check if user is authenticated and had deleted the account in other tab
  useEffect(() => {
    const handleAPILoginEnrichment = async () => {
      if (isLoading) return;
      if (!isAuthenticated) return;

      try {
        await identityLoginEnrichment();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.error_slug === 'customer_not_found') signOut();
      }
    };

    window.addEventListener('focus', handleAPILoginEnrichment);

    return () => {
      window.removeEventListener('focus', handleAPILoginEnrichment);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    identifyClarity();
  }, []);

  useEffect(() => {
    forceSessionStarted();
  }, []);

  useEffect(() => {
    appSessionTracking.fireTracking({
      onSessionStarted: () => trackGA({ eventName: 'appSessionStarted' }),
      onInfoTracked: (payload) => trackGA({ eventName: 'appInfoTracked', payload }),
    });
  }, []);

  let urlParams = window.location.search;

  if (urlParams) {
    urlParams = urlParams
      .slice(1)
      .split('&')
      .filter((param) => !param.startsWith('identify='))
      .join('&');

    setOnStorage('session', StorageMap.UrlParams, urlParams);
  }

  const location = useLocation();

  useEffect(() => {
    const companyName = 'Consumidor Positivo';
    document.title = titleMap[location.pathname] ? titleMap[location.pathname] + ' - ' + companyName : companyName;
  }, [location]);

  useEffect(() => {
    const startSourceUrl = getCookie('start_source_url');
    if (!startSourceUrl) {
      addCookie({
        key: 'start_source_url',
        value: window.location.href,
      });
    }

    sendEventExperimentsDogx();
  }, []);

  useEffect(() => {
    if (isProd()) {
      window.__APPVERSION__ = import.meta.env.VITE_DEPLOY_VERSION;
    }
  }, []);

  if (!isRenderInPartner && isLoading) return <LoaderCircular fullPage />;
  if (isRenderInPartner && isPartnerLoading) return <LoaderCircular fullPage />;

  return <Router />;
}

export default App;
